import { assessmentBenchmarkFragment } from "AppComponents/AssessmentLearningGoals/module/AssessmentLearningGoalsFragments";
import { gql } from "lib/apolloClient";
import { assessmentEvaluationFragment } from "modules/AssessmentTool/AssessmentToolFragment";
import {
  attachmentFragment,
  toolResponseFragment,
  pypElementRatingFragment,
  ibPypElementRatingFragment,
  studentsAlertFragment,
  userNameFragment,
  attachmentGroupsFragment,
  commonResolvedFieldValueFragment,
  fieldScoreFragment,
  plannerElementNodeFragment,
  pypElementFragment,
  subjectFragment,
  subjectGroupFragment,
  unitPlanFragment,
  staffFragment,
  studentFragment,
} from "modules/CommonFragments";

export const resolvedMinimalTreeFragment = gql`
  fragment resolvedMinimalTreeItem on ResolvedFieldValue {
    ... on ResolvedFieldUnitPlan {
      id
      type
      unitPlan {
        ...unitPlanBasicDetailsItem
      }
    }
    ...commonResolvedFieldValueItem
  }

  ${subjectGroupFragment.subjectGroupBasicDetails}
  ${plannerElementNodeFragment.plannerElementNode}
  ${assessmentBenchmarkFragment.benchmark}
  ${pypElementFragment.concept}
  ${pypElementFragment.action}
  ${pypElementFragment.lp}
  ${pypElementFragment.theme}
  ${pypElementFragment.atl}
  ${pypElementFragment.relatedConcept}
  ${subjectFragment.subjectBenchmarkDetails}
  ${attachmentFragment.attachment}
  ${attachmentGroupsFragment.attachmentGroup}
  ${fieldScoreFragment.fieldScore}
  ${unitPlanFragment.unitPlanBasicDetails}
  ${commonResolvedFieldValueFragment}
`;

export const studentDetailsFragment = gql`
  fragment studentItem on Student {
    ...userNameFields
    profileImage
    email
    isDevUser
    isArchived
    sourceId
  }
  ${userNameFragment}
`;

export const questionFieldsFragments = {
  questionFields: gql`
    fragment questionFieldsItem on QuestionFields {
      id
      uid
      value
      resolveFields {
        id
        ... on QuestionAttachment {
          id
          attachments {
            ...attachmentItem
          }
        }
        type
      }
      resolvedMinimalTree {
        ...resolvedMinimalTreeItem
      }
    }
    ${attachmentFragment.attachment}
    ${resolvedMinimalTreeFragment}
  `,
};

export const questionAttachmentFragments = {
  questionAttachment: gql`
    fragment questionAttachmentItem on Attachment {
      id
      thumbUrl
      mimeType
      name
      parentType
      questionAttachment {
        ... on QuestionSubmission {
          id
        }
      }
      similarityReport {
        submissionId
      }
      streamUrl
      title
      type
      isRead
      lockingState {
        state
        scheduledAt
      }
      createdBy {
        id
        type
      }
      url
      signedUrl
      metadata
    }
  `,
};

export const assessmentToolFragments = {
  questionAssessmentTool: gql`
    fragment questionAssessmentToolItem on AssessmentTool {
      id
      isStudentEvaluationEnable
      isTeacherEvaluationEnable
      assessmentToolItem {
        ...assessmentEvaluationItem
      }
      assessmentToolType
      isTeacherEvaluationEnable
      isStudentEvaluationEnable
    }
    ${assessmentEvaluationFragment.assessmentEvaluation}
  `,
};

export const questionFragments = {
  question: gql`
    fragment questionItem on Question {
      id
      questionType {
        id
        uid
        config
      }
      questionTitle {
        id
        uid
        value
      }
      libraryUid
      questionScore
      updatedAt
      metadata
      presetQuestionId
      assessmentTools {
        totalCount
        edges {
          node {
            ...questionAssessmentToolItem
          }
        }
      }
      fields {
        ...questionFieldsItem
      }
      answerFields {
        ...questionFieldsItem
      }
      librarySubjects {
        id
        label
      }
      associatedEntities {
        id
        ... on Subject {
          id
          name
        }
        ... on GenericTag {
          id
          label
        }
        ... on PlannerElementNode {
          id
          label
          depth
        }
      }
      aiConversation {
        edges {
          node {
            id
          }
        }
      }
    }
    ${questionFieldsFragments.questionFields}
    ${assessmentToolFragments.questionAssessmentTool}
  `,
  studentQuestionDetails: gql`
    fragment questionItem on Question {
      id
      questionType {
        id
        uid
        config
      }
      questionTitle {
        id
        uid
        value
      }
      questionScore
      assessmentTools @include(if: $showAssessmentTools) {
        totalCount
        edges {
          node {
            ...questionAssessmentToolItem
          }
        }
      }
      fields {
        ...questionFieldsItem
      }
      answerFields {
        ...questionFieldsItem
      }
    }
    ${questionFieldsFragments.questionFields}
    ${assessmentToolFragments.questionAssessmentTool}
  `,
  basicQuestion: gql`
    fragment questionItem on Question {
      id
      questionType {
        id
        uid
      }
    }
  `,
  questionDetails: gql`
    fragment questionDetailsItem on Question {
      id
      questionScore
      fields {
        ...questionFieldsItem
      }
      answerFields {
        ...questionFieldsItem
      }
      questionType {
        id
        uid
      }
    }
    ${questionFieldsFragments.questionFields}
  `,
  /**
   * @deprecated Use `questionInsightsDetailsV2` instead.
   * This is kept for backward compatibility.
   * It will be removed in future versions.
   */
  questionInsightsDetails: gql`
    fragment questionInsightsDetailsItem on Question {
      id
      questionScore
      fields {
        ...questionFieldsItem
      }
      answerFields {
        ...questionFieldsItem
      }
      questionType {
        id
        uid
      }
      assessmentTools {
        totalCount
        edges {
          node {
            id
            ...questionAssessmentToolItem
          }
        }
      }
      toolInsights {
        id
        toolId
        toolType
        insights {
          rowId
          columnId
          studentResponseCount
          teacherResponseCount
          studentsByTeacher {
            ...studentItem
          }
          studentsByStudent {
            ...studentItem
          }
        }
      }
    }
    ${questionFieldsFragments.questionFields}
    ${assessmentToolFragments.questionAssessmentTool}
    ${studentDetailsFragment}
  `,
  questionInsightsDetailsV2: gql`
    fragment questionInsightsDetailsItem on Question {
      id
      questionScoreV2
      parentIdV2
      parentTypeV2
      sequence
      questionType {
        id
        uid
      }
      questionTitleV2 {
        id
        uid
        value
      }
      fields {
        ...questionFieldsItem
      }
      answerFields {
        ...questionFieldsItem
      }
      assessmentTools {
        totalCount
        edges {
          node {
            id
            ...questionAssessmentToolItem
          }
        }
      }
      toolInsights {
        id
        toolId
        toolType
        insights {
          rowId
          columnId
          studentResponseCount
          teacherResponseCount
          studentsByTeacher {
            ...studentItem
          }
          studentsByStudent {
            ...studentItem
          }
        }
      }
    }
    ${questionFieldsFragments.questionFields}
    ${assessmentToolFragments.questionAssessmentTool}
    ${studentDetailsFragment}
  `,
  questionScoreAndToolDetails: gql`
    fragment questionScoreAndToolDetailsItem on Question {
      id
      questionType {
        id
        uid
      }
      questionScore
      fields {
        ...questionFieldsItem
      }
      assessmentTools {
        totalCount
        edges {
          node {
            id
            ...questionAssessmentToolItem
          }
        }
      }
    }
    ${questionFieldsFragments.questionFields}
    ${assessmentToolFragments.questionAssessmentTool}
  `,
  questionLibraryFeedDetails: gql`
    fragment questionLibraryFeedDetailsItem on Question {
      id
      questionType {
        id
        uid
      }
      questionScore
      questionTitle {
        id
        uid
        value
      }
      difficulty
      librarySubjects {
        id
        label
      }
      associatedEntities {
        id
        ... on GenericTag {
          id
          label
        }
        ... on PlannerElementNode {
          id
          label
          depth
        }
      }
    }
  `,
  questionLibraryFeedDetailsV2: gql`
    fragment questionLibraryFeedDetailsItem on Question {
      id
      questionType {
        id
        uid
      }
      questionScoreV2
      questionTitleV2 {
        id
        uid
        value
      }
      difficulty
      librarySubjects {
        id
        label
      }
      associatedEntities {
        id
        ... on GenericTag {
          id
          label
        }
        ... on PlannerElementNode {
          id
          label
          depth
        }
      }
    }
  `,
  questionV2: gql`
    fragment questionItem on Question {
      id
      questionType {
        id
        uid
        config
      }
      children {
        edges {
          id
          sequence
        }
      }
      questionTitleV2 {
        id
        uid
        value
      }
      parentIdV2
      parentTypeV2
      sequence
      libraryUid
      questionScoreV2
      updatedAt
      metadata
      presetQuestionId
      assessmentTools {
        totalCount
        edges {
          node {
            ...questionAssessmentToolItem
          }
        }
      }
      fields {
        ...questionFieldsItem
      }
      answerFields {
        ...questionFieldsItem
      }
      associatedEntities {
        id
        ... on Subject {
          id
          name
        }
        ... on GenericTag {
          id
          label
        }
        ... on PlannerElementNode {
          id
          label
          depth
        }
      }
      aiConversation {
        edges {
          node {
            id
          }
        }
      }
    }
    ${questionFieldsFragments.questionFields}
    ${assessmentToolFragments.questionAssessmentTool}
  `,
  studentQuestionDetailsV2: gql`
    fragment questionItem on Question {
      id
      questionType {
        id
        uid
        config
      }
      children {
        edges {
          id
          sequence
        }
      }
      questionTitleV2 {
        id
        uid
        value
      }
      parentIdV2
      parentTypeV2
      sequence
      questionScoreV2
      updatedAt
      presetQuestionId
      assessmentTools {
        totalCount
        edges {
          node {
            ...questionAssessmentToolItem
          }
        }
      }
      fields {
        ...questionFieldsItem
      }
      answerFields {
        ...questionFieldsItem
      }
    }
    ${questionFieldsFragments.questionFields}
    ${assessmentToolFragments.questionAssessmentTool}
  `,
};

export const organizationResourceQuestionFragment = gql`
  fragment organizationResourceQuestionItem on OrganizationResource {
    id
    resourceType
    item {
      ...questionItem
    }
    isSavedForLibrary
    isLibraryPremium
    showLibraryPremiumBannerForUser
    childrenV2(first: null, orderBy: DISPLAY_SEQUENCE, orderByDirection: ASC) {
      resourceTypeBasedCount {
        totalCount
      }
      edges {
        node {
          id
          item {
            ...questionItem
          }
        }
      }
    }
  }
  ${questionFragments.question}
`;

export const organizationResourceSectionFragment = gql`
  fragment organizationResourceSectionItem on OrganizationResource {
    id
    label
    type
    resourceType
    item {
      ... on Question {
        id
        fields {
          id
          uid
        }
      }
    }
    childrenV2(first: null, orderBy: DISPLAY_SEQUENCE, orderByDirection: ASC) {
      resourceTypeBasedCount {
        totalCount
      }
      edges {
        node {
          ...organizationResourceQuestionItem
        }
      }
    }
  }
  ${organizationResourceQuestionFragment}
`;

export const organizationResourceParentFragment = gql`
  fragment organizationResourceParentItem on OrganizationResource {
    id
    label
    type
    resourceType
    item {
      ... on Question {
        id
      }
    }
    childrenV2(first: null, orderBy: DISPLAY_SEQUENCE, orderByDirection: ASC) {
      resourceTypeBasedCount {
        totalCount
      }
      edges {
        node {
          ...organizationResourceSectionItem
        }
      }
    }
  }
  ${organizationResourceSectionFragment}
`;

export const questionSubmissionFragments = {
  questionSubmission: gql`
    fragment questionSubmissionItem on QuestionSubmission {
      id
      isQuestionAttempted
      fields {
        ...questionFieldsItem
      }
      question {
        id
      }
      toolResponses: toolResponsesV2(filters: { userFilter: $userFilter }) {
        id
        toolId
        toolType
        toolResponses {
          ...toolResponseItem
        }
      }
      ibPYPElementRatings {
        ...ratingItem
      }
      elementRemarks {
        ...remarkItem
      }
    }
    ${questionFieldsFragments.questionFields}
    ${toolResponseFragment.toolResponse}
    ${ibPypElementRatingFragment.studentElementRating}
    ${pypElementRatingFragment.elementRemark}
  `,
  questionSubmissionInsightsDetails: gql`
    fragment questionSubmissionInsightsDetailsItem on QuestionSubmission {
      id
      isQuestionAttempted
      question {
        id
      }
      fields {
        ...questionFieldsItem
      }
      ibPYPElementRatings {
        ...ratingItem
      }
      isQuestionAttempted
    }
    ${questionFieldsFragments.questionFields}
    ${ibPypElementRatingFragment.studentElementRating}
  `,
  questionSubmissionEvaluationDetails: gql`
    fragment questionSubmissionEvaluationDetailsItem on QuestionSubmission {
      id
      question {
        id
      }
      toolResponses: toolResponsesV2(filters: { userFilter: ALL }) {
        id
        toolId
        toolType
        toolResponses {
          ...toolResponseItem
        }
      }
      ibPYPElementRatings {
        ...ratingItem
      }
      elementRemarks {
        ...remarkItem
      }
    }
    ${toolResponseFragment.toolResponse}
    ${ibPypElementRatingFragment.studentElementRating}
    ${pypElementRatingFragment.elementRemark}
  `,
};

export const questionSubmissionConnectionFragments = {
  questionSubmissionConnection: gql`
    fragment questionSubmissionConnectionItem on QuestionSubmissionConnection {
      totalCount
      totalQuestionCount
      totalQuestionAttempted
      edges {
        node {
          ...questionSubmissionItem
        }
      }
    }
    ${questionSubmissionFragments.questionSubmission}
  `,
};

export const organizationResourceQuestionFragmentV2 = gql`
  fragment organizationResourceQuestionItem on OrganizationResource {
    id
    resourceType
    item {
      ...questionItem
    }
    childrenV2(first: null, orderBy: DISPLAY_SEQUENCE, orderByDirection: ASC) {
      resourceTypeBasedCount {
        totalCount
      }
      edges {
        node {
          id
          item {
            ...questionItem
          }
        }
      }
    }
  }
  ${questionFragments.studentQuestionDetails}
`;

export const organizationResourceSectionFragmentV2 = gql`
  fragment organizationResourceSectionItem on OrganizationResource {
    id
    label
    resourceType
    childrenV2(first: null, orderBy: DISPLAY_SEQUENCE, orderByDirection: ASC) {
      resourceTypeBasedCount {
        totalCount
      }
      edges {
        node {
          ...organizationResourceQuestionItem
        }
      }
    }
  }
  ${organizationResourceQuestionFragmentV2}
`;

export const organizationResourceParentFragmentV2 = gql`
  fragment organizationResourceParentItem on OrganizationResource {
    id
    label
    resourceType
    childrenV2(first: null, orderBy: DISPLAY_SEQUENCE, orderByDirection: ASC) {
      resourceTypeBasedCount {
        totalCount
      }
      edges {
        node {
          ...organizationResourceSectionItem
        }
      }
    }
  }
  ${organizationResourceSectionFragmentV2}
`;

export const studentAssigmentSubmissionWithEvaluationV2 = gql`
  fragment studentAssigmentSubmissionItem on StudentAssignmentSubmission {
    id
    response
    questionSubmissions {
      totalCount
      totalQuestionCount
      totalQuestionAttempted
    }
    status
    statusV2
    submittedAt
    returnedAt
    isPaused
    endTime
    isLockScreenEnabled
    isOnScreenCalculatorEnabled
    isDraftSubmission
    evaluations {
      toolResponses(filters: { userFilter: $userFilter }) {
        id
        toolId
        toolType
        toolResponses {
          ...toolResponseItem
        }
      }
      ibPYPElementRatings {
        id
        value
        ibPYPElementType
      }
      isEvaluated
      isSharedWithStudent
    }
  }
  ${toolResponseFragment.toolResponse}
`;

export const assignedStudentFieldsFragment = gql`
  fragment assignedStudentItem on StudentAssignment {
    id
    student {
      ...studentItem
      ... on Student {
        id
        alertTypes {
          ...alertTypeFields
        }
      }
    }
    isSubmitted
    isEvaluated
    isSharedWithStudent
    isEvaluationApplicable
    toolResponses: toolResponsesV2(filters: { userFilter: ALL }) {
      id
      toolId
      toolType
      toolResponses {
        ...toolResponseItem
      }
    }
    ibPYPElementRatings {
      id
      value
      ibPYPElementId
      ibPYPElementType
      academicCriteriaValue {
        id
        label
      }
      academicCriteriaSet {
        id
      }
    }
    submission {
      id
      questionSubmissions {
        edges {
          node {
            ...questionSubmissionInsightsDetailsItem
          }
        }
      }
    }
  }
  ${studentDetailsFragment}
  ${questionSubmissionFragments.questionSubmissionInsightsDetails}
  ${toolResponseFragment.toolResponse}
  ${studentsAlertFragment}
`;

export const libraryQuestionFragment = gql`
  fragment libraryQuestionItem on Question {
    id
    questionType {
      id
      uid
      config
    }
    children {
      edges {
        ...questionItem
      }
    }
    questionTitleV2 {
      id
      uid
      value
    }
    parentIdV2
    parentTypeV2
    sequence
    libraryUid
    questionScoreV2
    updatedAt
    metadata
    presetQuestionId
    assessmentTools {
      totalCount
      edges {
        node {
          ...questionAssessmentToolItem
        }
      }
    }
    fields {
      ...questionFieldsItem
    }
    answerFields {
      ...questionFieldsItem
    }
    associatedEntities {
      id
      ... on Subject {
        id
        name
      }
      ... on GenericTag {
        id
        label
      }
      ... on PlannerElementNode {
        id
        label
        depth
      }
    }
    aiConversation {
      edges {
        node {
          id
        }
      }
    }
  }
  ${questionFragments.questionV2}
  ${questionFieldsFragments.questionFields}
  ${assessmentToolFragments.questionAssessmentTool}
`;

export const aiMessageFragment = {
  aiMessage: gql`
    fragment aiMessageItem on AiMessage {
      id
      role
      authorType
      content
      metadata
      createdBy {
        ...staffItem
        ...studentItem
      }
      votes {
        myVote
      }
      usageMetricsId
      parentMessage {
        id
      }
      attachments {
        id
        name
        type
        mimeType
        url
        thumbUrl
        signedUrl
        title
        metadata
        streamUrl
        parentType
        createdBy {
          id
          type
        }
        presetAttachment {
          id
        }
        createdAt
      }
    }
    ${staffFragment.basicStaffDetails}
    ${studentFragment.basicStudentDetails}
  `,
};
